// Week 6 — Assert a capability inside a mutation. If the current role + opts
// don't grant the permission, throws a CapabilityError. UI already hides the
// triggering button; this is defense-in-depth against URL / keyboard / replay
// access.

import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from './useAuth'
import { useTeam } from './useTeam'
import { qk } from './queryKeys'
import { can, getRole } from '../lib/permissions'
import type { Permission } from '../lib/permissions'
import type { AppState } from '../types/firestore'

export class CapabilityError extends Error {
  constructor(perm: Permission) {
    super(`Blocked: you don't have the "${perm}" permission.`)
    this.name = 'CapabilityError'
  }
}

/** Returns an `assertCan(perm)` function that throws if the permission is denied. */
export function useAssertCan() {
  const qc = useQueryClient()
  const { uid, user } = useAuth()
  const { team } = useTeam()
  return (perm: Permission): void => {
    if (!uid) throw new CapabilityError(perm)
    const state = qc.getQueryData<AppState>(qk.appstate(uid))
    if (!state) return // state not loaded yet — let the caller's existing guards handle it
    const role = getRole(state.opts, {
      userEmail: user?.email ?? null,
      elevatedMembers: team?.elevatedMembers,
    })
    if (!can(perm, role, state.opts)) throw new CapabilityError(perm)
  }
}
